import React, { useMemo, useRef, useState } from 'react';
import {
  CampaignBanner as SimbaBanner,
  type CampaignBannerProps as SimbaBannerProps,
} from '@naf-simba/sections-campaign-banner';
import { PortableText } from '@portabletext/react';
import { resolveLinkProps } from '@lib/resolveLinkProps';
import { getHostname } from '@lib/getHostname';
import useSelector from '../../../redux/typedHooks';
import { useDocumentUrlWithNode } from '../../../hooks/Documents/useDocumentUrl';
import { CampaignBlockType } from '../../../../../types/campaignBlockType';
import { useWindowResize } from '../../../hooks/Window/useWindowResize';
import { useCloudinary } from '../../../hooks/Cloudinary/useCloudinary';
import { useTrackCTA } from '../../../utils/Tracking/CtaClick/TrackCtaClick';
import { useTrackPromotion } from '../../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../../utils/Tracking/Promotion/getPromotionEventTrackingData';

export const CampaignBlock = ({ data }: { data: CampaignBlockType }) => <CampaignBanner campaign={data} />;

interface CampaignBannerProps {
  campaign: CampaignBlockType;
}

const legacyVariantToBannerSize: Record<CampaignBlockType['variant'], SimbaBannerProps['size']> = {
  large: 'lg',
  medium: 'md',
  small: 'sm',
};

// TODO: add type export from @naf-simba/sections-campaign-banner
type BannerAction = SimbaBannerProps['primaryAction'];

export const CampaignBanner = ({
  campaign: {
    illustration,
    referenceComponent,
    image,
    blockType,
    label,
    title,
    outlineButton,
    blank,
    buttonText,
    url,
    ingress,
    anchorId,
    variant,
  },
}: CampaignBannerProps) => {
  const campaignUrl = useDocumentUrlWithNode(referenceComponent);

  const trackCTA = useTrackCTA();
  const baseUrl = useSelector((state) => state.application.baseUrl);

  const bannerRef = useRef<HTMLDivElement>(null);
  const { getResponsiveImage } = useCloudinary();
  const publicId = image?.public_id || image?.publicId;
  const [mainImage, setMainImage] = useState(getResponsiveImage(publicId, { width: 500 })?.toURL());

  const setResponsiveImage = useMemo(
    () => () => {
      if (image) {
        setMainImage(getResponsiveImage(publicId, { width: 500 })?.toURL());
      }
    },
    [image, getResponsiveImage, publicId],
  );

  useWindowResize(() => {
    setResponsiveImage();
  });

  const effectiveUrl = campaignUrl || url;
  let finalUrl = effectiveUrl;
  if (anchorId && finalUrl) {
    finalUrl = `${finalUrl}#${anchorId}`;
  }

  const linkProps = resolveLinkProps({ url: finalUrl, baseUrl, blank });
  const isInternalCTA = 'to' in linkProps;

  const promotion = getPromotionEventTrackingData(buttonText, finalUrl);
  const shouldTrackPromotion = Boolean(promotion);
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'campaign',
    creative_name: `CTA | ${variant} | ${buttonText}`,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
  });

  const handleCtaTracking = () => {
    if (shouldTrackPromotion) {
      trackSelectPromotion();
    }
    trackCTA({
      cta_position: 'content',
      text: buttonText,
      url: finalUrl,
      cta_hostname: isInternalCTA ? getHostname(baseUrl) : getHostname(url),
    });
  };

  const actionProps: BannerAction = buttonText
    ? {
        label: buttonText,
        onClick: handleCtaTracking,
        ...linkProps,
      }
    : undefined;

  return (
    <div ref={shouldTrackPromotion ? wrapperRef : undefined}>
      <SimbaBanner
        label={label}
        title={title}
        description={ingress && <PortableText value={ingress} />}
        descriptionAs="div"
        size={legacyVariantToBannerSize[variant]}
        ref={bannerRef}
        color={blockType === 'secondary' ? 'secondary' : 'primary'}
        primaryAction={!outlineButton ? actionProps : undefined}
        secondaryAction={outlineButton ? actionProps : undefined}
        media={
          mainImage && {
            src: mainImage || '',
            alt: image?.source_altText || '',
            fit: illustration ? 'contain' : 'cover',
          }
        }
      />
    </div>
  );
};
